import { useCallback } from "react";
import { useLocation } from "react-router-dom";

import { Box, Typography } from "@mui/material";

import { ModelType } from "../../../API";
import { useDateRange } from "../../../common/components/datePicker/modelsDatePickers";
import { MediaFullScreen } from "../../../common/components/media/MediaFullScreen";
import TableCellTruncated from "../../../common/components/table/TableCellTruncated";
import { useCustomerIdGuard } from "../../../common/hooks/useCustomerIdGuard";
import useLocationFromCache from "../../../common/hooks/useLocationFromCache";
import { RouteEnum } from "../../../common/models/enums";
import { UpdateEventFeedbackInput } from "../../../common/types/events/api";
import { formatTimestamp } from "../../../common/utils/timestampUtils";
import {
  DEFAULT_MEDIA_ICON_STYLES,
  modelToServiceMap,
} from "../../../common/variables/common";
import {
  errorNotification,
  successNotification,
} from "../../../common/variables/notification";
import { useSelectedLocation } from "../../../common/variables/selectedLocation";
import { useSelectedModel } from "../../../common/variables/selectedModel";
import { useSelectedDataHubCamera } from "../../data-hub/components/dataHub.variable";
import LiveViewButtonContainer from "../../telemetry/components/LiveViewButtonContainer";
import FeedbackDetailsTableCellContainer from "../components/feedback/FeedbackDetailsTableCellContainer";
import FeedbackThumbsTableCellContainer from "../components/feedback/FeedbackThumbsTableCellContainer";
import { EventTableColumn } from "../components/table/EventTable";
import { EventViewModel } from "../models/eventViewModel";
import { RecordingViewModel } from "../models/recordingViewModel";
import { useUpdateEventFeedback } from "./events/useUpdateEventFeedback";
import { HARDHAT_SELECT_OPTION } from "./export/useGetRecordingsToExport";
import { useFeedbackUpdateConfirmDialog } from "./useFeedbackUpdateConfirmDialog";

export const useBasicEventTableColumns = (
  newestRecording?: RecordingViewModel
) => {
  const customerId = useCustomerIdGuard();
  let modelId = useSelectedModel();
  const facilityId = useSelectedLocation();
  const cameraVariable = useSelectedDataHubCamera();
  const dateRange = useDateRange();

  const { pathname } = useLocation();

  if (
    pathname.includes(RouteEnum.TelemetryHardHat) ||
    pathname.includes(RouteEnum.SensoryTelemetryHardHat)
  ) {
    modelId = HARDHAT_SELECT_OPTION;
  }

  const { updateEventFeedback, loading, error } = useUpdateEventFeedback({
    customerId,
    modelId: modelId?.value ?? "",
    facilityId: facilityId?.value ?? "",
    deviceId: cameraVariable?.value ?? "",
    fromDate: dateRange?.from ? dateRange.from.toISOString() : "",
  });

  const { getCachedLocationName } = useLocationFromCache();

  const handleFeedbackUpdate = useFeedbackUpdateConfirmDialog(
    updateEventFeedback,
    error
  );

  const columns: EventTableColumn<EventViewModel>[] = [
    {
      title: "Event",
      width: "300px",
      content: event => (
        <Box
          sx={{
            display: "flex",
            width: "300px",
            alignItems: "center",
            gap: "0.75em",
          }}
        >
          <MediaFullScreen
            keyFrame={newestRecording?.keyFrame}
            mediaOutput={newestRecording?.mediaOutput}
            s3ImageFullScreenProps={{
              sx: DEFAULT_MEDIA_ICON_STYLES,
              fixedHeight: "75px",
            }}
          />

          <Box sx={{ display: "flex", flexDirection: "column" }}>
            <Typography variant="buttonMedium">
              {modelToServiceMap[event.modelId.toLowerCase() as ModelType]}
            </Typography>

            <Typography variant="body2Regular" color="text.secondary">
              {formatTimestamp(newestRecording?.timestamp ?? event.timestamp)}
            </Typography>
          </Box>
        </Box>
      ),
    },
    {
      title: "Location",
      width: "160px",
      content: event => {
        const locationName = getCachedLocationName(event.location);

        return (
          <TableCellTruncated
            tooltipText={locationName}
            text={locationName}
            TypographyProps={{ variant: "body2" }}
          />
        );
      },
    },
    {
      title: "Feedback",
      width: "140px",
      content: event => {
        const onUpdateEventFeedback = useCallback(
          (input: UpdateEventFeedbackInput) =>
            handleFeedbackUpdate(input, event),
          [handleFeedbackUpdate, event]
        );

        return (
          <FeedbackThumbsTableCellContainer
            event={event}
            onUpdateEventFeedback={onUpdateEventFeedback}
          />
        );
      },
    },
    {
      title: "Feedback details",
      width: "980px",
      content: event => {
        const memoizedCallback = useCallback(
          async (input: UpdateEventFeedbackInput) => {
            const result = await updateEventFeedback(input);

            if (error || !result) {
              errorNotification("Something went wrong");
            } else {
              successNotification(
                "Event feedback details updated successfully"
              );
            }
          },
          [updateEventFeedback, error]
        );

        return (
          <FeedbackDetailsTableCellContainer
            loading={loading}
            event={event}
            onUpdateEventFeedback={memoizedCallback}
          />
        );
      },
    },
  ];

  if (pathname.includes(RouteEnum.TelemetryHardHat)) {
    columns.push({
      title: "",
      width: "140px",
      content: event => {
        const cameraId = event.camera_id || "";
        const nodeId = event.nodeId || "";

        return <LiveViewButtonContainer cameraId={cameraId} nodeId={nodeId} />;
      },
    });
  }

  return { columns };
};
